import angular, {IHttpProvider, ILogService, IQService} from "angular";
import {regNavn} from "../tjenester/tredjepartsTjenestenavn";
import _ from "lodash";
import {Konfigurasjon} from "./Konfigurasjon";
import {Bygginformasjon} from "./Bygginformasjon";
import {Kontekst} from "../tjenester/Kontekst";
import {Debugger} from 'debug'
import IProvideService = angular.auto.IProvideService;
import IInjectorService = angular.auto.IInjectorService;
import {registrerHeadere} from "./StandardRequestHeadereInterceptor";
import {erLikeObjekter} from "felles-ng2/støttefunksjoner/objekter";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";

const debug:Debugger = require('debug')('http');
const logTregeRequestesGrense = 1000; // ett sekund

export function logHttpKallInterceptorForInjisering() {
    return [
        regNavn.ng.$httpProvider,
        ($httpProvider: IHttpProvider) =>
            $httpProvider.interceptors.push([regNavn.ng.$q, regNavn.ng.$log, logHttpKallInterceptor])
    ];
}

/** Interceptor som logger http-requester til konsole samt trege requester til server*/
function logHttpKallInterceptor($q: IQService, $log: ILogService) {
    return {
        request: function (konfig) {
            if (konfig.data || konfig.params)
                debug('%s %s data:%O params:%O', konfig.method, konfig.url, konfig.data, konfig.params);
            else
                debug('%s %s', konfig.method, konfig.url);

            konfig.msBeforeAjaxCall = new Date().getTime();
            return konfig;
        },

        // The response function is called after receiving a good response from the server
        response: function (response) {
            var msAfterAjaxCall = new Date().getTime();
            var timeTakenInMs = msAfterAjaxCall - response.config.msBeforeAjaxCall;
            if (timeTakenInMs > logTregeRequestesGrense) //bare log requester som tar lang tid
                $log.info(
                    {
                        url: response.config.url,
                        timeTakenInMs: timeTakenInMs
                    });
            return response;
        }
    }
}


export function dekorer$logForInjisering() {
    return [
        regNavn.ng.$provide,
        ($provide: IProvideService) =>
            $provide.decorator(regNavn.ng.$log, [Logger.regNavn, regNavn.ng.$delegate, dekorer$log])
    ];
}

/** Dekorerer angulars $log tjeneste slik at denne sender logmeldinger til server. */
function dekorer$log(logger: Logger, orginalLogger) {
    var dekorer = (nivå) => {
        var orginal = orginalLogger[nivå];
        orginalLogger[nivå] = (...args) => {
            orginal.apply(orginalLogger, args);
            logger[nivå].apply(logger, args);
        };
        orginalLogger[nivå].logs = orginal.logs; //for ikke å brekke angular-mocks.js
    };

    dekorer("log");
    dekorer("warn");
    dekorer("info");
    dekorer("error");
    dekorer("debug");

    return orginalLogger;
}

/** Tjeneste som sender log-meldinger til server */
export class Logger {
    static regNavn = "Logger";

    static forInjisering() {
        return [
            regNavn.ng.$injector,
            Konfigurasjon.regNavn,
            Bygginformasjon.regNavn,
            Logger
        ]
    }

    /** Ikke send log meldinger under "warn" nivå oftere enn en gang per 5 sekund */
    public throttleDelay = 5000;
    /** Ikke send log meldinger på "warn" eller høyere nivå oftere enn en gang per 1 sekund */
    public throttleDelayErrorOgWarn = 1000;

    private ventendeMeldinger:Logmelding[] = [];
    private ajaxUrl;
    private throttledSend;
    private throttledSendErrorOgWarn;

    constructor(private $injector: IInjectorService,
                private konfigurasjon: Konfigurasjon,
                private bygginformasjon: Bygginformasjon) {
        this.ajaxUrl = konfigurasjon.serviceRotUrl + '/api/logging';
        //ok å ikke benytte Throttle her da vi ikke trenger dypere angular integrasjon
        this.throttledSend = _.throttle(
            () => this.sendAlleMeldinger(),
            this.throttleDelay,
            {leading: false, trailing: true}
        );
        this.throttledSendErrorOgWarn = _.throttle(
          () => this.sendAlleMeldinger(),
          this.throttleDelayErrorOgWarn,
          {leading: false, trailing: true}
        );
    }

    //offentlige metoder

    log(msg) {
        this.registrerMelding(msg, "trace");
        this.throttledSend();
    }

    debug(msg) {
        this.registrerMelding(msg, "debug");
        this.throttledSend();
    }

    info(msg) {
        this.registrerMelding(msg, "info");
        this.throttledSend();
    }

    warn(msg) {
        this.registrerMelding(msg, "warn");
        this.throttledSendErrorOgWarn();
    }

    error(msg) {
        this.registrerMelding(msg, "error");
        this.throttledSendErrorOgWarn();
    }

    //hjelpemetoder

    registrerMelding(melding: any, nivå: string) {
        var kontekst = this.kontekst();
        var msg = this.meldingSomStreng(melding);
        //MERK at endringer i nøklene må speiles i no.systemfabrikken.lederplan.resources.logging.LoggingRessurs.Melding
        this.ventendeMeldinger.push(
            {
                msg: msg.msg,
                type: msg.type,
                stack: msg.stack,
                lvl: nivå,
                ts: new Date().getTime(),
                bn: this.bygginformasjon.version,
                usri: kontekst.erAutentisert() ? kontekst.autentisertBruker().id : "",
                usrn: kontekst.erAutentisert() ? kontekst.autentisertBruker().brukernavn : "",
                url: window.location.href
            });
    }

    meldingSomStreng(melding): { msg: string; stack: string; type: 'string' | 'number'| 'boolean'|'object'|'json'|'error'|'unknown'|'undefined'} {
        switch (typeof melding) {
            case "string":
                return {msg: melding, type: "string", stack: ""};
            case "number":
                return {msg: melding.toString(), type: "number", stack: ""};
            case "boolean":
                return {msg: melding.toString(), type: "boolean", stack: ""};
            case "undefined":
                return {msg: melding, type: "undefined", stack: ""};
            case "object":
                if ((melding instanceof RegExp) ||
                    (melding instanceof String) ||
                    (melding instanceof Number) ||
                    (melding instanceof Boolean))
                    return {msg: melding.toString(), type: "object", stack: ""};

                else if (melding instanceof Error)
                    return this.ekstraherFeilmelding(melding);
                else
                    return {msg: angular.toJson(melding), type: "json", stack: ""};
            default:
                return {msg: melding.toString(), type: "unknown", stack: ""};
        }

    }

    private ekstraherFeilmelding(feil: Error): { msg: string; stack: string; type: "error" } {
        if(feil.message.indexOf("ngRepeat:dupes") >= 0){
            // ngDupes eksponerer for mye data til server, fjerner alt fra og med strengen "Duplicate values"
            // Eksempel på streng som ngDupes-meldingen i utgangspunktet logger, value kan f.eks. inneholde objekter med kundedata:
            // Error: [ngRepeat:dupes] Duplicates in a repeater are not allowed. Use 'track by' expression to specify unique keys. Repeater: value in [4, 4], Duplicate key: number:4, Duplicate value: 4\nhttps://errors.angularjs.org/1.8.2/ngRepeat/dupes?p0=value%20in%20%5B4%2C%204%5D&p1=number%3A4&p2=4\n    at http://localhost:8888/app/vendor.js:59855:14\n    at ngRepeatAction (http://localhost:8888/app/vendor.js:91792:23)\n    at $watchCollectionAction (http://localhost:8888/app/vendor.js:77598:15)\n    at Scope.$digest (http://localhost:8888/app/vendor.js:77734:25)\n    at Scope.$apply (http://localhost:8888/app/vendor.js:78081:26)\n    at bootstrapApply (http://localhost:8888/app/vendor.js:61570:15)\n    at Object.invoke (http://localhost:8888/app/vendor.js:64592:21)\n    at doBootstrap (http://localhost:8888/app/vendor.js:61569:16)\n    at Object.bootstrap (http://localhost:8888/app/vendor.js:61584:14)\n    at 41644 (http://localhost:8888/app/main.js:22814:48)"
            // Etter sanitering står Error: [ngRepeat:dupes] Duplicates in a repeater are not allowed. Use 'track by' expression to specify unique keys. Repeater: value in [4, 4], Duplicate key: number:4, igjen
            let messageDuplicateValueStart = feil.message.indexOf("Duplicate value:");
            let stackDuplicateValuesStart = feil.stack.indexOf("Duplicate value:");
            return {msg: `${feil.message.substring(0, messageDuplicateValueStart)} - videre data filtrert ut`, stack: `${feil.stack.substring(0, stackDuplicateValuesStart)} - videre data filtrert ut`, type: "error"}
        }
        return {msg: feil.message, stack: feil.stack, type: "error"};
    }

    sendAlleMeldinger() {
        //pga throttledSend kan køen være tom
        if (this.ventendeMeldinger.length === 0) return;

        var meldinger = this.meldingerTilSending(this.ventendeMeldinger);
        this.ventendeMeldinger = [];

        var meldingerSomJson = angular.toJson(meldinger);

        var xhr = new XMLHttpRequest();
        xhr.open('POST', this.ajaxUrl);
        xhr.setRequestHeader('Content-Type', 'application/json');
        registrerHeadere(this.$injector, this.konfigurasjon, this.bygginformasjon, (navn, verdi) => xhr.setRequestHeader(navn, verdi));
        xhr.send(meldingerSomJson);
        xhr.onerror = () => {
            console.error("En feil oppstod under sending av logger til server", meldinger);
            //this.ventendeMeldinger.push(meldinger); skal vi prøve på nytt?
        };
        xhr.onreadystatechange = () => {
            if (xhr.status < 400) return;
            console.error("En feil oppstod under sending av logger til server, server svarte med " + xhr.status);
            //this.ventendeMeldinger.push(meldinger); skal vi prøve på nytt?
        }
    }

    kontekst() {
        return <Kontekst>this.$injector.get(Kontekst.regNavn);
    }

    //hjelpemetoder

    /** Returnerer en ny liste med meldinger der duplikater er fjernet.
     * Melding anses som duplikat dersom alle felter med unntak av ts (tidsstempel) er likt. */
    private meldingerTilSending(meldinger: Logmelding[]):(Logmelding & {rep:number})[]{
        const utenDuplikater: {melding: Logmelding, tilSammenlikning: Exclude<Logmelding, 'ts'>, duplikater: Logmelding[]}[] = [];
        meldinger.forEach(melding => {
            const meldingTilSammenlikning = this.meldingUtenTs(melding);
            const duplikat = utenDuplikater.find(kandidat => erLikeObjekter(kandidat.tilSammenlikning, meldingTilSammenlikning))
            if (erNil(duplikat))
                utenDuplikater.push({melding: melding, tilSammenlikning: meldingTilSammenlikning, duplikater: []});
            else
                duplikat.duplikater.push(melding);
        });

        return utenDuplikater.map(meldingOgDuplikater =>
            ({...meldingOgDuplikater.melding, rep: meldingOgDuplikater.duplikater.length}))
    }

    private meldingUtenTs(melding: Logmelding): Exclude<Logmelding, 'ts'> {
        const kopi = {...melding};
        delete kopi.ts
        return kopi;
    }
}

interface Logmelding {
    msg: string;
    type: string;
    stack: string;
    lvl: string;
    ts: number;
    bn: string;
    usri: string;
    usrn: string;
    url: string;
}

/** Visse http-feilkoder skal kun logges til konsoll og ikke til server */
export function feilkodeSomKunLoggesTilKonsoll(feil: { status: number }): boolean {
    return feil.status === HttpResponskoder.UNAUTHORIZED || //401 - bruker ikke logget inn
      feil.status === HttpResponskoder.GONE || //410 - når id som ikke finnes i database forespørres
      feil.status === HttpResponskoder.INTERNAL_SERVER_ERROR || //500 - generell serverfeil
      feil.status === HttpResponskoder.SERVICE_UNAVAILABLE     //503 - nginx throttling
}

